import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import { Button } from "@/components/ui/button";
import { Sparkles, Upload, Wand2, Download, ArrowRight, ShieldCheck, Heart, Lock, Trash2 } from "lucide-react";

const HOW = [
  { icon: Sparkles, title: "Pick your product", desc: "Choose from coloring books, storybooks and keepsakes." },
  { icon: Upload, title: "Upload your memories", desc: "Add your favorite photos and a few details." },
  { icon: Wand2, title: "We create the magic", desc: "Our studio crafts your one-of-a-kind keepsake." },
  { icon: Download, title: "Download your keepsake", desc: "Receive a beautiful printable PDF in your account." },
];

export default function Home() {
  const [featured, setFeatured] = useState([]);
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    api.get("/products", { params: { featured: true } }).then((r) => setFeatured(r.data.slice(0, 4)));
    api.get("/categories").then((r) => setCategories(r.data));
  }, []);

  return (
    <div>
      {/* Hero */}
      <section className="relative overflow-hidden mfm-grain">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 pt-14 pb-8 lg:pt-24 lg:pb-16 grid lg:grid-cols-2 gap-12 items-center">
          <div className="fade-up">
            <span className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-amber-700 bg-amber-100 rounded-full px-3 py-1">
              <Heart className="w-3.5 h-3.5" /> Personalized from your photos
            </span>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.08] text-slateink mt-5">
              Turn Your Photos Into a <span className="text-amber">Personalized Coloring Book</span>
            </h1>
            <p className="text-base sm:text-lg text-slate-600 leading-relaxed mt-5 max-w-lg">
              Upload your favorite photos and we'll transform them into a one-of-a-kind printable keepsake made just for you.
            </p>
            <p className="text-sm font-semibold text-amber-700 mt-4">Personalized books from $29</p>
            <div className="flex flex-wrap gap-3 mt-6">
              <Button asChild size="lg" data-testid="create-book-hero-cta" className="rounded-full bg-amber hover:bg-amber-600 text-white font-semibold text-base px-7 h-12">
                <Link to="/product/personalized-coloring-book">Create My Book <ArrowRight className="w-4 h-4 ml-1" /></Link>
              </Button>
              <Button asChild size="lg" variant="outline" data-testid="free-preview-hero-cta" className="rounded-full border-slate-300 text-slateink font-semibold text-base px-7 h-12 hover:bg-white">
                <Link to="/free-preview">Try a Free Photo Preview</Link>
              </Button>
            </div>
            <div className="flex flex-wrap items-center gap-x-6 gap-y-2 mt-8 text-sm text-slate-500">
              <div className="flex items-center gap-1.5"><Lock className="w-4 h-4 text-sage" /> Secure checkout</div>
              <div className="flex items-center gap-1.5"><ShieldCheck className="w-4 h-4 text-sage" /> Private photo handling</div>
              <div className="flex items-center gap-1.5"><Download className="w-4 h-4 text-sage" /> Digital delivery</div>
            </div>
          </div>

          {/* Transformation demo */}
          <div className="fade-up" style={{ animationDelay: "0.15s" }}>
            <div className="grid grid-cols-3 gap-2 sm:gap-3 items-center">
              <TransformCard label="PHOTO" img="https://images.unsplash.com/photo-1643240143978-9563c3760ca4?crop=entropy&cs=srgb&fm=jpg&q=85&w=500" />
              <div className="hidden" />
              <TransformCard label="ILLUSTRATION" img="https://images.unsplash.com/photo-1616802099190-5a4485319df6?crop=entropy&cs=srgb&fm=jpg&q=85&w=500" grayscale />
              <TransformCard label="FINISHED BOOK" img="https://images.unsplash.com/photo-1611837897664-75bdacb6cf9f?crop=entropy&cs=srgb&fm=jpg&q=85&w=500" wide />
            </div>
            <div className="hidden sm:flex justify-center gap-2 mt-4 text-xs font-semibold text-amber-700/70">
              PHOTO <ArrowRight className="w-4 h-4" /> PERSONALIZED ILLUSTRATION <ArrowRight className="w-4 h-4" /> FINISHED BOOK
            </div>
            <p className="text-center text-[11px] text-slate-400 mt-2">Illustrative demo — your book is created from your own photos.</p>
          </div>
        </div>
      </section>

      {/* Trust section */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 pt-14">
        <div className="text-center max-w-2xl mx-auto mb-8">
          <h2 className="font-display text-3xl sm:text-4xl font-bold text-slateink">Your memories stay yours</h2>
        </div>
        <div className="grid sm:grid-cols-3 gap-5">
          {[
            { icon: Lock, title: "Private by default", desc: "Your uploaded images are accessible only for fulfilling your order." },
            { icon: ShieldCheck, title: "No marketing without permission", desc: "We never use your family's photos in ads or social media unless you explicitly opt in." },
            { icon: Trash2, title: "Automatic photo deletion", desc: "Original uploads are automatically deleted after the retention period following delivery." },
          ].map((t, i) => (
            <div key={i} className="bg-white rounded-2xl p-6 border border-slate-100">
              <div className="w-11 h-11 rounded-xl bg-sage/10 text-sage flex items-center justify-center mb-3"><t.icon className="w-5 h-5" /></div>
              <h3 className="font-display text-lg font-bold text-slateink">{t.title}</h3>
              <p className="text-sm text-slate-600 mt-2 leading-relaxed">{t.desc}</p>
            </div>
          ))}
        </div>
        <div className="text-center mt-5">
          <Link to="/policies/photos" className="text-sm text-amber font-semibold">Learn about photo privacy →</Link>
        </div>
      </section>

      {/* How it works */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-16">
        <div className="text-center max-w-2xl mx-auto mb-12">
          <h2 className="font-display text-3xl sm:text-4xl font-bold text-slateink">How It Works</h2>
          <p className="text-slate-600 mt-3">Four simple steps from photo to finished keepsake.</p>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {HOW.map((s, i) => (
            <div key={i} className="bg-white rounded-2xl p-6 border border-slate-100 card-hover">
              <div className="w-12 h-12 rounded-xl bg-amber-100 text-amber flex items-center justify-center mb-4"><s.icon className="w-6 h-6" /></div>
              <p className="text-xs font-bold text-amber-700/60">STEP {i + 1}</p>
              <h3 className="font-display text-lg font-bold text-slateink mt-1">{s.title}</h3>
              <p className="text-sm text-slate-600 mt-2 leading-relaxed">{s.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Categories */}
      <section className="bg-subtle py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center max-w-2xl mx-auto mb-10">
            <h2 className="font-display text-3xl sm:text-4xl font-bold text-slateink">Made for the people you love.</h2>
            <p className="text-slate-600 mt-3">A keepsake for every relationship and every occasion.</p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
            {categories.map((c) => (
              <Link key={c.key} to={`/shop?category=${encodeURIComponent(c.key)}`} data-testid={`category-${c.key.toLowerCase()}`}
                className="group relative rounded-2xl overflow-hidden aspect-[4/3] card-hover">
                <img src={c.image} alt={c.label} loading="lazy" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                <div className="absolute inset-0 bg-gradient-to-t from-slateink/70 to-transparent" />
                <div className="absolute bottom-3 left-4">
                  <p className="text-white font-display text-lg font-bold">{c.label}</p>
                  <p className="text-white/70 text-xs">{c.count} product{c.count === 1 ? "" : "s"}</p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Featured products */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-16">
        <div className="flex items-end justify-between mb-8">
          <div>
            <h2 className="font-display text-3xl sm:text-4xl font-bold text-slateink">Bestselling keepsakes</h2>
            <p className="text-slate-600 mt-2">Our most-loved personalized products.</p>
          </div>
          <Button asChild variant="ghost" className="text-amber font-semibold hidden sm:inline-flex"><Link to="/shop">View all <ArrowRight className="w-4 h-4 ml-1" /></Link></Button>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
          {featured.map((p) => <ProductCard key={p.id} product={p} />)}
        </div>
      </section>

      {/* Free preview CTA */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 pb-16">
        <div className="rounded-3xl bg-slateink text-white overflow-hidden grid md:grid-cols-2 items-center">
          <div className="p-8 sm:p-12">
            <h2 className="font-display text-3xl sm:text-4xl font-bold leading-tight">See Your Photo as a Coloring Page</h2>
            <p className="text-slate-300 mt-4 leading-relaxed">Upload one photo and get a free watermarked preview instantly. Love it? Turn it into a complete personalized book.</p>
            <Button asChild size="lg" data-testid="free-preview-cta" className="rounded-full bg-amber hover:bg-amber-600 text-white font-semibold mt-7 h-12 px-7">
              <Link to="/free-preview">Try it free <ArrowRight className="w-4 h-4 ml-1" /></Link>
            </Button>
          </div>
          <div className="hidden md:block h-full min-h-[280px]">
            <img src="https://images.unsplash.com/photo-1616802099190-5a4485319df6?crop=entropy&cs=srgb&fm=jpg&q=85&w=900" alt="Coloring page preview" className="w-full h-full object-cover" />
          </div>
        </div>
      </section>
    </div>
  );
}

function TransformCard({ label, img, grayscale, wide }) {
  return (
    <div className={`rounded-2xl overflow-hidden bg-white border border-slate-100 shadow-sm ${wide ? "col-span-3 sm:col-span-1 animate-float" : ""}`}>
      <div className="aspect-square overflow-hidden">
        <img src={img} alt={label} className={`w-full h-full object-cover ${grayscale ? "grayscale contrast-125" : ""}`} />
      </div>
      <p className="text-[10px] font-bold tracking-wide text-center text-amber-700/70 py-1.5">{label}</p>
    </div>
  );
}
